import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { Automations } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { type AutomationRun } from '~/hooks/queries/automations/usePrivateAutomationRunsList';

export type PrivateWorkspaceAgentPanelAutomationLatestRun = AutomationRun | undefined;

export const PRIVATE_AUTOMATION_LATEST_RUN_KEY = 'PRIVATE_AUTOMATION_LATEST_RUN';

export type PrivateAutomationLatestRunKey = [
  typeof PRIVATE_AUTOMATION_LATEST_RUN_KEY,
  { automationId: string; workspaceId: string | undefined },
];

export const getPrivateAutomationLatestRunKey = ({
  automationId,
  workspaceId,
}: {
  automationId: string;
  workspaceId: string | undefined;
}): PrivateAutomationLatestRunKey => [PRIVATE_AUTOMATION_LATEST_RUN_KEY, { automationId, workspaceId }];

export type UsePrivateWorkspaceAgentPanelAutomationLatestRunsParams = {
  automationIds: string[];
  enabled: boolean;
  workspaceId: string | undefined;
};

export const usePrivateWorkspaceAgentPanelAutomationLatestRuns = ({
  automationIds,
  enabled,
  workspaceId,
}: UsePrivateWorkspaceAgentPanelAutomationLatestRunsParams) => {
  const queries = useQueries({
    queries: automationIds.map((automationId) => ({
      queryKey: getPrivateAutomationLatestRunKey({ automationId, workspaceId }),
      queryFn: async () =>
        callAirApi(() =>
          Automations.listRuns({
            automationId,
            limit: 1,
            workspaceId: workspaceId ?? '',
          }),
        ),
      enabled: enabled && Boolean(workspaceId),
      staleTime: 30_000,
    })),
  });

  const latestRunByAutomationId = useMemo(() => {
    const map = new Map<string, PrivateWorkspaceAgentPanelAutomationLatestRun>();

    automationIds.forEach((automationId, index) => {
      const run = queries[index]?.data?.data[0];
      map.set(automationId, run);
    });

    return map;
  }, [automationIds, queries]);

  const isLoadingLatestRuns = enabled && queries.some((query) => query.isPending);

  return {
    isLoadingLatestRuns,
    latestRunByAutomationId,
  };
};
